<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { ExclamationCircleIcon, QuestionMarkCircleIcon, CheckIcon } from '@heroicons/vue/20/solid';

type Size = "xs" | "sm" | "md" | "lg" | "xl"
const props = withDefaults(defineProps<{
  value?: string | null;
  label?: string;
  description?: string;
  placeholder?: string;
  validateFn?: (data: string) => boolean;
  icon?: any;
  tooltip?: string;
  absoluteText?: string;
  size?: Size;
}>(), {
  type: "primary", 
  size: "sm",
  disabled: false,
});

const emits = defineEmits<{
  (e: 'update:modelValue', textInput?: string | null): void
}>();

const textInput = ref(props.value || "");
const hasError = ref(false);
const showTooltip = ref(false);

const isValid = computed(() => props.validateFn ? props.validateFn(textInput.value) : null)

const inputClasses = computed(() => {
  if (props.validateFn ? props.validateFn(textInput.value) : null) return "block w-full rounded-md border-0 placeholder-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 "
  return hasError.value 
    ? "block w-full rounded-md border-0 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500"
    : "block w-full rounded-md border-0 placeholder-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500";
});


watch(textInput, (newValue) => emits("update:modelValue", newValue))

// Watch for external changes to value and update the local ref
watch(() => props.value, (newValue) => {
  if (textInput.value !== newValue && newValue != null) {
    textInput.value = newValue;
  }
});

const sizeClass = computed(() => {
  switch (props.size) {
    case "xs": return " py-1 text-xs  leading-6";
    case "sm": return " py-1.5 text-sm leading-6";
    case "md": return " py-2 text-base leading-7";
    case "lg": return " py-2.5 text-lg leading-8";
    case "xl": return "px-4 py-3 text-xl leading-9";
  }
})
</script>
<template>
  <div class="w-full text-left">
    <div>
      <label v-if="label" for="date" class="block text-sm font-medium leading-6 text-gray-900">{{ props.label }} :</label>
      <span v-if="props.description" class="block text-xs italic text-slate-600">({{ props.description }})</span>
    </div>
    <div class="inline-flex items-center justify-center w-full">
      <div class="relative w-full mt-2 rounded-md shadow-sm">
        <input 
          @focusin="showTooltip = true"
          @focusout="showTooltip = false"
          @keydown="(event: KeyboardEvent) => {
            showTooltip = false;
          }"
          v-model="textInput" 
          type="text"
          :class="[sizeClass, inputClasses, icon ? 'pl-9' : 'pr-9', 'block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600']"
          :aria-invalid="hasError"
          :placeholder="props.placeholder || '(vide)'"
          :disabled="props.disabled"
          v-bind:readonly="props.disabled"
        />
        <div v-if="isValid && !icon" class="absolute inset-y-0 z-10 flex items-center pointer-events-none right-2">
          <CheckIcon class="w-5 h-5 text-green-600" aria-hidden="true" />
        </div>
        <div
          v-else-if="tooltip"
          @mouseover="showTooltip = true"
          v-tooltip.bottom="{
            shown: showTooltip,
            content: tooltip,
            triggers: ['hover', 'focus'],
          }" class="absolute inset-y-0 z-50 flex items-center pointer-events-none right-2">
          <QuestionMarkCircleIcon class="w-5 h-5 text-slate-400" aria-hidden="true" />
        </div>
        <div v-else-if="absoluteText" :class="sizeClass" class="absolute inset-y-0 z-50 flex items-center pointer-events-none right-2 text-slate-700">
          <span class="px-2"> | </span>
          <span class="font-semibold">{{ absoluteText }}</span>
        </div>

        <div v-if="hasError" class="absolute inset-y-0 flex items-center pr-3 pointer-events-none right-5">
          <ExclamationCircleIcon class="w-5 h-5 text-red-500" aria-hidden="true" />
        </div>
        <div v-else-if="icon" class="absolute inset-y-0 flex items-center pr-3 pointer-events-none left-2">
          <component :is="icon" class="w-5 h-5 text-slate-400" aria-hidden="true" />
        </div>
      </div>
    </div>

    <p v-if="hasError" class="mt-2 text-sm text-red-600" id="price-error">Merci d'entrer un format valide.</p>
  </div>
</template>
